// pages/admin/AdminDashboard.js

import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ChartBarIcon, 
  UserGroupIcon, 
  HomeIcon, 
  DocumentTextIcon,
  ChatBubbleLeftRightIcon,
  SparklesIcon,
  BoltIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

// Components
import StatsCard from '../../components/admin/StatsCard';
import AIInsightsPanel from '../../components/admin/AIInsightsPanel';
import RealtimeChart from '../../components/admin/RealtimeChart';
import QuickActions from '../../components/admin/QuickActions';
import RecentActivity from '../../components/admin/RecentActivity';
import SystemAlerts from '../../components/admin/SystemAlerts';

// Redux
import { 
  fetchDashboardStats, 
  selectDashboardStats, 
  selectDashboardLoading 
} from '../../redux/slices/adminSlice';

const AdminDashboard = () => {
  const dispatch = useDispatch();
  const stats = useSelector(selectDashboardStats);

  const loading = useSelector(selectDashboardLoading);
  
  // Local state
  const [activeTab, setActiveTab] = useState('overview');
  const [realtimeData, setRealtimeData] = useState({});
  const [showAIPanel, setShowAIPanel] = useState(false);
  const [aiInsights, setAiInsights] = useState([]);

  useEffect(() => {
    dispatch(fetchDashboardStats());
    
    // Setup real-time updates
    const interval = setInterval(() => {
      fetchRealtimeData();
    }, 30000); // Update every 30 seconds

    return () => clearInterval(interval);
  }, [dispatch]);

  const fetchRealtimeData = async () => {
    try {
      const response = await fetch('/api/admin/dashboard/realtime/stats', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      if (data.success) {
        setRealtimeData(data.data);
      }
    } catch (error) {
      console.error('Failed to fetch realtime data:', error);
    }
  };

  const handleQuickAction = async (action) => {
    try {
      const response = await fetch(`/api/admin/dashboard/quick-actions/${action}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      
      const data = await response.json();
      if (data.success) {
        // Show success toast
        alert(data.data.message);
      }
    } catch (error) {
      console.error('Quick action failed:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
          className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full"
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 bg-clip-text text-transparent">
            ThinkRealty Dashboard
            </h1>
            <p className="text-gray-600 mt-2 text-lg">
              Real-time insights and intelligent automation for your real estate business
            </p>
          </div>
          
          <div className="flex items-center gap-4 mt-4 lg:mt-0">
            {/* Real-time Status */}
            <div className="flex items-center gap-2 bg-green-100 px-4 py-2 rounded-full">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              <span className="text-green-700 font-semibold text-sm">System Online</span>
            </div>
            
            
          </div>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatsCard
          title="Total Users"
          value={stats?.totalUsers}
          icon={<UserGroupIcon className="w-8 h-8" />}
          color="blue"
          gradient="from-blue-500 to-cyan-500"
        />
        <StatsCard
          title="Properties"
          value={stats?.totalProperties}
          icon={<HomeIcon className="w-8 h-8" />}
          color="green"
          gradient="from-green-500 to-emerald-500"
        />
        <StatsCard
          title="Active Agents"
          value={stats?.totalAgents}
          icon={<UserGroupIcon className="w-8 h-8" />}
          color="purple"
          gradient="from-purple-500 to-pink-500"
        />
        <StatsCard
          title="Featured Properties"
          value={stats?.featuredProperties}
          icon={<UserGroupIcon className="w-8 h-8" />}
          color="orange"
          gradient="from-orange-500 to-red-500"
        />
      </div>

      {/* AI Insights Panel */}
      <AnimatePresence>
        {showAIPanel && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mb-8"
          >
            <AIInsightsPanel insights={stats?.aiInsights || []} />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Content Grid */}
      

    </div>
  );
};

export default AdminDashboard;
